<template>
  <div class="relative-position q-mt-sm" @click="markAsRead()">
    <q-item
      class="text-right items-center absolute-right bg-grey-1 rounded-borders"
      v-ripple
      :class="{
        'justify-start': currentX < swipeActionThreshold,
        'justify-end': currentX > swipeActionThreshold,
      }"
      :style="{
        width: `${-currentX}px`,
      }"
    >
      <q-icon :name="tabOutlineMailOpened" size="md" color="accent" />
    </q-item>
    <q-item
      class="bg-white rounded-borders q-pa-md items-center"
      :to="
        getLink(
          notification.data.type
            ? notification.data
            : { ...notification.data, type: notification.type }
        )
      "
      @touchstart.passive="(e) => startSwipe(e, notification.isRead)"
      @touchmove.passive="onSwipe"
      @touchend.passive="endSwipe"
      v-ripple
    >
      <q-item-section avatar>
        <Avatar :uid="getUserUid(notification.type, notification.data)" />
      </q-item-section>
      <q-item-section>
        <div class="row justify-between items-center q-pb-xs">
          <div class="text-weight-medium">
            {{ getTitle(notification) }}
          </div>
          <div class="text-caption text-grey">
            {{ moment.unix(notification.createdAt.seconds).fromNow() }}
          </div>
        </div>
        <q-item
          no-wrap
          class="row justify-between items-center q-pa-none q-gutter-x-md"
          style="min-height: auto; word-break: break-word"
        >
          <div class="ellipsis-3-lines">
            {{ getContent(notification) }}
          </div>
          <q-badge
            v-if="!notification.isRead"
            color="accent"
            style="min-height: 4px"
            class="q-pa-xs"
          />
        </q-item>
      </q-item-section>
    </q-item>
  </div>
</template>

<script setup>
import moment from 'moment';
import { formatPlayerTime } from '@/utils/formatDate';
import { useStore } from 'vuex';
import Avatar from '@/components/brand/Avatar.vue';
import { ref } from 'vue';
import { tabOutlineMailOpened } from 'quasar-extras-svg-icons/tabler-icons-v3';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { getLink, getTitle, getUserUid } from '@/utils/notifications';

const swipeActionThreshold = -160;
const props = defineProps({
  notification: {
    type: Object,
    required: true,
  },
  markAsRead: {
    type: Function,
    required: false,
    default: () => {},
  },
});
const $store = useStore();

const swipeStartX = ref(0);
const currentX = ref(0);

const startSwipe = (event, isRead) => {
  if (isRead) return;
  swipeStartX.value = event.touches[0].clientX;
};

const onSwipe = (event) => {
  if (swipeStartX.value) {
    const deltaX = event.touches[0].clientX - swipeStartX.value;
    if (
      deltaX < swipeActionThreshold &&
      currentX.value >= swipeActionThreshold
    ) {
      Haptics.impact({ style: ImpactStyle.Medium });
    }
    currentX.value = deltaX;
    currentX.value = Math.min(0, deltaX);
    event.currentTarget.style.transform = `translateX(${currentX.value}px)`;
  }
};

const endSwipe = (event) => {
  const deltaX = currentX.value;

  if (deltaX < swipeActionThreshold) {
    props.markAsRead();
  }
  event.currentTarget.style.transform = 'translateX(0)';
  swipeStartX.value = 0;
  currentX.value = 0;
};

const getContent = (notification) => {
  switch (notification.type) {
    case 'thread':
    case 'reply': {
      return `${
        $store.getters['getUserProfile'](props.notification.data.addedBy)
          ?.name || 'User'
      } commented '${props.notification.data.comment}' on ${formatRecording(
        props.notification.data
      )} `;
    }
    case 'recording-failed':
      return 'Processing meeting was failed';
    case 'recording-added': {
      return optionalAddBy(
        `${formatRecording(props.notification.data)} was added`,
        props.notification.data.addedBy
      );
    }
    case 'new-reaction': {
      return optionalAddBy(
        `New reaction: '${props.notification.data.value}'`,
        props.notification.data.addedBy
      );
    }
    case 'new-member': {
      const newMember = $store.getters['getUserProfile'](
        props.notification.data.newMember
      );

      return optionalAddBy(
        `${newMember?.name || 'New Member'} was added`,
        props.notification.data.invitedBy
      );
    }
    case 'member-removed': {
      return optionalAddBy(
        '1 member was removed',
        props.notification.data.removedBy
      );
    }
    case 'mention': {
      return optionalAddBy(
        `New mention: '${props.notification.data.comment}'`,
        props.notification.data.mentionedBy
      );
    }
  }
};

const formatRecording = ({ recordingId, recordingName }) => {
  const recording = $store.getters['recs/getRecording'](recordingId);
  if (recording) {
    return `"${recording.name}"(${formatPlayerTime(
      recording.duration / 1000
    )})`;
  } else if (recordingName) {
    return `"${recordingName}"`;
  } else {
    return 'New Meeting';
  }
};

const optionalAddBy = (text, uid) => {
  const by = $store.getters['getUserProfile'](uid);
  if (by && by.name) {
    return `${text} by ${by.name}`;
  } else {
    return text;
  }
};
</script>

<style>
.q-item {
  transition: transform 0.3s ease;
}
.limited-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}
</style>
